import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { PowerMark } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import type { NavEntry } from './adminNavigation';
import type { NavCountValue } from './useNavCounts';

interface NavEntryLinkProps {
  entry: NavEntry;
  active: boolean;
  className: string;
  onNavigate?: () => void;
  title?: string;
  ariaLabel?: string;
  children: ReactNode;
}

/** Link auf einen Navigationseintrag, samt Suchparametern (Reinigung › Woche). */
export function NavEntryLink({
  entry,
  active,
  className,
  onNavigate,
  title,
  ariaLabel,
  children,
}: NavEntryLinkProps) {
  return (
    <Link
      to={entry.to}
      search={entry.search ?? {}}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      aria-label={ariaLabel}
      title={title}
      className={className}
    >
      {children}
    </Link>
  );
}

/** Zähler neben einem Eintrag. Rot nur, wenn heute jemand etwas tun muss. */
export function NavCount({
  value,
  hot,
  className,
}: {
  value: NavCountValue;
  hot?: boolean;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'rounded-pill inline-flex h-[22px] min-w-[22px] flex-none items-center justify-center px-1.5 text-[11px] font-bold',
        value === 'error'
          ? 'bg-amber-tint text-ink'
          : hot
            ? 'bg-sealwax text-white'
            : 'bg-card-alt text-ink',
        className,
      )}
      title={value === 'error' ? 'Anzahl konnte nicht geladen werden' : undefined}
    >
      {value === 'error' ? '!' : value}
    </span>
  );
}

/** Zeichen von StayOn: der Einschaltknopf in Signalgrün auf weißer Kachel. */
export function ShellLogo({ size = 38 }: { size?: number }) {
  return (
    <span
      className="border-hairline flex flex-none items-center justify-center rounded-md border bg-white"
      style={{ width: size, height: size }}
    >
      <PowerMark
        size={Math.round(size * 0.68)}
        color="var(--color-signal)"
        cutoutColor="var(--color-card)"
      />
    </span>
  );
}
